import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import RawHeader from "../components/RawHeader";
import AdminNav from "../components/AdminNav";
import { api } from "../lib/api";

const AdminDiscounts = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stripeOk, setStripeOk] = useState(null); // null=unknown, true=ok, false=bad
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [percentOff, setPercentOff] = useState("");
  const [amountOff, setAmountOff] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    // Ping Stripe first so we can show a clear banner if the key is invalid
    try {
      const p = await api.get("/admin/stripe/ping");
      setStripeOk(!!p.data?.ok);
    } catch { setStripeOk(false); }
    try {
      const r = await api.get("/admin/discounts");
      setCodes(r.data?.promotion_codes || []);
    } catch (e) {
      if (stripeOk !== false) {
        toast.error(e?.response?.data?.detail || "Failed to load discounts");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!code || code.length < 3) return toast.error("Code must be 3+ chars");
    const pct = parseFloat(percentOff);
    const amt = parseFloat(amountOff);
    if (!percentOff && !amountOff) return toast.error("Enter percent OR amount off");
    if (percentOff && amountOff) return toast.error("Only one of percent / amount");
    setCreating(true);
    try {
      const body = {
        code: code.trim().toUpperCase(),
        name: name || undefined,
        max_redemptions: maxRedemptions ? parseInt(maxRedemptions, 10) : undefined,
      };
      if (percentOff) body.percent_off = pct;
      else body.amount_off_gbp = amt;
      await api.post("/admin/discounts", body);
      toast.success(`Created ${body.code}`);
      setCode(""); setName(""); setPercentOff(""); setAmountOff(""); setMaxRedemptions("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this promo code?")) return;
    try {
      await api.delete(`/admin/discounts/${id}`);
      toast.success("Deactivated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen text-white" data-testid="admin-discounts-page">
      <RawHeader variant="admin" />
      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
        <AdminNav />
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// DISCOUNT CODES</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-8 text-[#d4af37]">Promotion Codes</h1>

        {stripeOk === false && (
          <div data-testid="stripe-key-warning" className="mb-8 p-4 border border-red-500 bg-red-500/10">
            <div className="text-[10px] tracking-[0.3em] uppercase text-red-400 mb-1">// STRIPE KEY INVALID</div>
            <p className="text-sm text-zinc-200">
              The <code className="text-[#d4af37]">STRIPE_API_KEY</code> in the backend environment is not accepted by Stripe.
              Discount CRUD and live checkouts will fail until you rotate the key in the Emergent Deployment Secrets panel and redeploy.
            </p>
          </div>
        )}

        {/* Create form */}
        <form onSubmit={create} className="raw-panel p-5 mb-8 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="discount-create-form">
          <input data-testid="discount-code-input" value={code} onChange={(e)=>setCode(e.target.value.toUpperCase())} placeholder="CODE (e.g. RAW10)" className="bg-black border border-zinc-700 px-3 py-2 text-sm md:col-span-2 focus:border-[#d4af37] outline-none" />
          <input data-testid="discount-name-input" value={name} onChange={(e)=>setName(e.target.value)} placeholder="Label (optional)" className="bg-black border border-zinc-700 px-3 py-2 text-sm md:col-span-2 focus:border-[#d4af37] outline-none" />
          <input data-testid="discount-percent-input" type="number" step="1" min="1" max="100" value={percentOff} onChange={(e)=>{setPercentOff(e.target.value); if(e.target.value) setAmountOff("");}} placeholder="% off (1-100)" className="bg-black border border-zinc-700 px-3 py-2 text-sm focus:border-[#d4af37] outline-none" />
          <input data-testid="discount-amount-input" type="number" step="0.01" min="0.5" value={amountOff} onChange={(e)=>{setAmountOff(e.target.value); if(e.target.value) setPercentOff("");}} placeholder="£ off" className="bg-black border border-zinc-700 px-3 py-2 text-sm focus:border-[#d4af37] outline-none" />
          <input data-testid="discount-max-input" type="number" step="1" min="1" value={maxRedemptions} onChange={(e)=>setMaxRedemptions(e.target.value)} placeholder="Max uses (optional)" className="bg-black border border-zinc-700 px-3 py-2 text-sm md:col-span-5 focus:border-[#d4af37] outline-none" />
          <button data-testid="discount-create-btn" disabled={creating} type="submit" className="btn-raw btn-raw-primary">
            {creating ? "CREATING…" : "+ CREATE"}
          </button>
        </form>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-zinc-600 text-xs tracking-[0.3em] uppercase">LOADING…</div>
        ) : codes.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-12 text-center text-zinc-500 text-xs tracking-[0.3em] uppercase">
            NO PROMO CODES YET — CREATE ONE ABOVE
          </div>
        ) : (
          <div className="raw-panel divide-y divide-zinc-800" data-testid="discounts-list">
            {codes.map((c) => (
              <div key={c.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`discount-row-${c.code}`}>
                <div className="flex-1 min-w-[140px]">
                  <div className="font-mono text-lg text-[#d4af37]">{c.code}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{c.name || "—"}</div>
                </div>
                <div className="text-sm text-zinc-300 font-mono">
                  {c.percent_off ? `${c.percent_off}% off` : c.amount_off ? `£${c.amount_off.toFixed(2)} off` : "—"}
                </div>
                <div className="text-xs text-zinc-500">
                  Used {c.times_redeemed}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}
                </div>
                <div className={`text-[10px] tracking-[0.3em] uppercase px-2 py-1 border ${c.active ? "text-[#d4af37] border-[#d4af37]" : "text-zinc-600 border-zinc-700"}`}>
                  {c.active ? "ACTIVE" : "INACTIVE"}
                </div>
                {c.active && (
                  <button
                    data-testid={`discount-deactivate-${c.code}`}
                    onClick={() => deactivate(c.id)}
                    className="btn-raw text-xs"
                  >
                    DEACTIVATE
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-zinc-500 mt-6">
          Codes apply at Stripe checkout — customers can enter them in the promo field on the payment page.
        </p>
      </section>
    </div>
  );
};

export default AdminDiscounts;
